import { useEffect, useRef, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Download, BookOpen, Image as ImageIcon, X } from "lucide-react";

const Card = ({ status, title, description, onDownload, busy }) => (
  <div data-testid={`pdf-card-${status}`} className="border border-[#e5e1d8] bg-[#f9f8f6] p-6 sm:p-8 flex flex-col">
    <div className="flex items-center gap-3 mb-3">
      <BookOpen size={18} className="text-[#c5a873]" />
      <h3 className="font-serif text-xl sm:text-2xl text-[#1a1918]">{title}</h3>
    </div>
    <p className="font-serif italic text-[#4a4843] mb-6 leading-relaxed flex-1">{description}</p>
    <button
      data-testid={`btn-download-${status}`}
      onClick={onDownload}
      disabled={busy}
      className="flex items-center justify-center gap-2 bg-[#1a1918] text-[#f9f8f6] hover:bg-[#c5a873] hover:text-[#1a1918] transition-colors px-5 py-3 font-sans text-[10px] sm:text-xs tracking-[0.3em] uppercase disabled:opacity-50"
    >
      <Download size={14} /> {busy ? "generazione…" : "scarica PDF"}
    </button>
  </div>
);

const AdminExport = () => {
  const [busy, setBusy] = useState(null);
  const [coverPath, setCoverPath] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/book-cover");
        setCoverPath(data.path || null);
      } catch {}
    })();
  }, []);

  const uploadCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCoverBusy(true);
    const tid = toast.loading("caricamento copertina…");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/book-cover", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCoverPath(data.path);
      toast.success("Copertina aggiornata", { id: tid });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore", { id: tid });
    } finally {
      setCoverBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeCover = async () => {
    if (!window.confirm("Tornare alla copertina di default (illustrazione albero)?")) return;
    setCoverBusy(true);
    try {
      await api.delete("/admin/book-cover");
      setCoverPath(null);
      toast.success("Copertina rimossa: tornerà l'illustrazione di default");
    } catch (err) {
      toast.error("Errore");
    } finally {
      setCoverBusy(false);
    }
  };

  const download = async (status) => {
    setBusy(status);
    const tid = toast.loading("generazione del libro…");
    try {
      const res = await api.get("/admin/export-pdf", {
        params: { status },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const disposition = res.headers["content-disposition"] || "";
      const m = disposition.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `attraversando-${status}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF scaricato", { id: tid });
    } catch (e) {
      let msg = "Errore durante l'export";
      try {
        if (e?.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          msg = j.detail || msg;
        }
      } catch {}
      toast.error(msg, { id: tid });
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminShell title="Libro PDF">
      <div className="max-w-4xl">
        <p className="font-serif italic text-base sm:text-lg text-[#4a4843] mb-8 sm:mb-10 leading-relaxed">
          Genera un libro in formato PDF a partire dai post. Formato A5, tipografia editoriale (Cormorant Garamond), copertina avorio con illustrazione, indice automatico. L'operazione è riservata agli autori — i lettori non hanno accesso a questo file.
        </p>

        <div className="border border-[#e5e1d8] bg-[#f9f8f6] p-5 sm:p-6 mb-10" data-testid="book-cover-block">
          <div className="flex items-center gap-3 mb-4">
            <ImageIcon size={18} className="text-[#c5a873]" />
            <h3 className="font-serif text-xl sm:text-2xl text-[#1a1918]">Copertina del libro</h3>
          </div>
          <p className="font-serif italic text-[#4a4843] text-sm sm:text-base leading-relaxed mb-5">
            Per impostazione predefinita la copertina mostra un'illustrazione di un albero ad acquerello su sfondo avorio. Puoi caricare un'immagine personalizzata (PNG/JPG/WEBP) — verrà usata al centro della copertina al posto dell'albero.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-32 sm:w-40 aspect-[3/4] border border-[#e5e1d8] bg-[#f3ecd2] flex items-center justify-center overflow-hidden">
              {coverPath ? (
                <img
                  src={fileUrl(coverPath)}
                  alt="copertina libro"
                  data-testid="book-cover-preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843]/60 px-3 text-center">
                  default · albero
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <button
                data-testid="btn-book-cover-upload"
                onClick={() => fileRef.current?.click()}
                disabled={coverBusy}
                className="flex items-center justify-center gap-2 bg-[#1a1918] text-[#f9f8f6] hover:bg-[#c5a873] hover:text-[#1a1918] transition-colors px-5 py-2.5 font-sans text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
              >
                {coverPath ? "sostituisci" : "carica copertina"}
              </button>
              {coverPath && (
                <button
                  data-testid="btn-book-cover-remove"
                  onClick={removeCover}
                  disabled={coverBusy}
                  className="flex items-center justify-center gap-2 border border-[#9e4747] text-[#9e4747] hover:bg-[#9e4747] hover:text-[#f9f8f6] transition-colors px-5 py-2.5 font-sans text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                >
                  <X size={12} /> torna al default
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={uploadCover}
                data-testid="book-cover-input"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <Card
            status="published"
            title="Antologia dei pubblicati"
            description="Solo i post già pubblicati, ordinati per data. Il libro 'finito' da condividere, stampare o in futuro eventualmente vendere."
            onDownload={() => download("published")}
            busy={busy === "published"}
          />
          <Card
            status="drafts"
            title="Quaderno delle bozze"
            description="Tutti i post ancora da sistemare. Utile per rileggerli su carta o offline, magari fuori casa, prima di pubblicarli."
            onDownload={() => download("drafts")}
            busy={busy === "drafts"}
          />
          <Card
            status="all"
            title="Archivio completo"
            description="Pubblicati + bozze, tutto in un unico volume. Lo specchio dell'intero diario in un colpo solo."
            onDownload={() => download("all")}
            busy={busy === "all"}
          />
        </div>

        <div className="mt-10 sm:mt-14 p-5 sm:p-6 bg-[#f2efe9] border-l-2 border-[#d4b886]">
          <p className="font-sans text-[10px] sm:text-xs tracking-[0.25em] uppercase text-[#4a4843] mb-2">Suggerimenti</p>
          <ul className="font-serif italic text-[#4a4843] leading-relaxed space-y-1 text-sm sm:text-base">
            <li>• La generazione può richiedere qualche secondo — con molte centinaia di post anche un minuto.</li>
            <li>• Le immagini di copertina e inline vengono incluse nel PDF.</li>
            <li>• L'autore mostrato in copertina è quello più frequente fra i post inclusi.</li>
            <li>• Per un e-book vendibile serve poi aprire il PDF con un impaginatore (Affinity Publisher, InDesign) o convertirlo in ePub.</li>
          </ul>
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminExport;
